import React, { useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Stack,
  Divider,
  Container,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  Store as StoreIcon,
  AdminPanelSettings as AdminIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useAuth } from "../../AuthContext";
import { updateProfile } from "firebase/auth";
import { useNotification } from "../../contexts/NotificationContext";

const ProfilePage: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const { showSuccess, showError } = useNotification();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState("");

  if (!currentUser) {
    return null;
  }

  const handleEditClick = () => {
    setNewDisplayName(currentUser.displayName || "");
    setEditDialogOpen(true);
  };

  const handleSaveProfile = async () => {
    try {
      await updateProfile(currentUser, {
        displayName: newDisplayName,
      });
      showSuccess("プロフィールを更新しました。");
      setEditDialogOpen(false);
      // Force refresh or state update might be needed depending on AuthContext implementation,
      // but Firebase auth state change should trigger re-render if listener is active.
    } catch (error) {
      console.error("Profile update error", error);
      showError("プロフィールの更新に失敗しました。");
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={4}
      >
        <Typography variant="h4" component="h1" fontWeight="bold">
          プロフィール
        </Typography>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={handleEditClick}
        >
          編集
        </Button>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 0,
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box
          sx={{ bgcolor: "primary.main", height: 120, position: "relative" }}
        >
          <Avatar
            src={currentUser.photoURL || undefined}
            alt={currentUser.displayName || "User"}
            sx={{
              width: 100,
              height: 100,
              position: "absolute",
              bottom: -50,
              left: 32,
              border: "4px solid white",
              bgcolor: "secondary.main",
              fontSize: "2.5rem",
            }}
          >
            {currentUser.displayName ? (
              currentUser.displayName[0].toUpperCase()
            ) : (
              <PersonIcon fontSize="inherit" />
            )}
          </Avatar>
        </Box>

        <Box sx={{ mt: 7, px: 4, pb: 4 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" fontWeight="bold">
              {currentUser.displayName || "ゲストユーザー"}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {currentUser.email}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} sx={{ mb: 4 }}>
            {userProfile?.roles.map((role) => (
              <Chip
                key={role}
                icon={role === "admin" ? <AdminIcon /> : <PersonIcon />}
                label={role.toUpperCase()}
                color={role === "admin" ? "error" : "primary"}
                variant="outlined"
                size="small"
              />
            ))}
          </Stack>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            アカウント詳細
          </Typography>

          <Stack spacing={2}>
            <Box display="flex" alignItems="center">
              <BadgeIcon color="action" sx={{ mr: 2 }} />
              <Box>
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                >
                  ユーザーID
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                  {userProfile?.user_id || currentUser.uid}
                </Typography>
              </Box>
            </Box>

            <Box display="flex" alignItems="center">
              <EmailIcon color="action" sx={{ mr: 2 }} />
              <Box>
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                >
                  メールアドレス
                </Typography>
                <Typography variant="body1">{currentUser.email}</Typography>
              </Box>
            </Box>

            {userProfile?.store_id && (
              <Box display="flex" alignItems="center">
                <StoreIcon color="action" sx={{ mr: 2 }} />
                <Box>
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                  >
                    所属店舗ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                    {userProfile.store_id}
                  </Typography>
                </Box>
              </Box>
            )}
          </Stack>
        </Box>
      </Paper>

      {/* Edit Profile Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>プロフィール編集</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="表示名"
            type="text"
            fullWidth
            variant="outlined"
            value={newDisplayName}
            onChange={(e) => setNewDisplayName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleSaveProfile} variant="contained">
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProfilePage;
